import React, { useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Input } from "./input";
import { generateUID } from "../utils/generateUID";
import { FieldGrid } from "./form";

const EMPTY_ARRAY = [];

const safeGenerateUID = () =>
  typeof generateUID === "function" ? generateUID() : `uid_${Date.now()}`;

const idToName = (id, list = [], key = "printable_name") => {
  if (!Array.isArray(list)) return id;
  const found = list.find((x) => String(x?.id) === String(id));
  return found ? found[key] : id;
};

export const SizeChartTable = ({
  methods,
  sizesOptions = [],
  columnOptions = [],
}) => {
  const { watch, setValue, getValues } = methods;
  const sizes = watch("size") || EMPTY_ARRAY;
  const chart_columns = watch("chart_columns") || EMPTY_ARRAY;

  useEffect(() => {
    const existingData = getValues("size_chart_data") || [];
    const existingColumns = [
      ...new Set(
        existingData.flatMap((row) =>
          (row.column_data || []).map((c) => c.column_name)
        )
      ),
    ];

    // If chart_columns is empty, populate it from existing data
    if (!getValues("chart_columns")?.length && existingColumns.length) {
      setValue("chart_columns", existingColumns);
    }
  }, [getValues, setValue]);

  useEffect(() => {
    if (!sizes.length || !chart_columns.length) return;

    const currentData = getValues("size_chart_data") || [];

    const newData = sizes.map((sz) => {
      const existingRow = currentData.find((r) => r.size === sz);
      const base = {
        code: existingRow?.code || safeGenerateUID(),
        printable_name: existingRow?.printable_name || safeGenerateUID(),
        size: sz,
        id: existingRow?.id,
      };

      return {
        ...base,
        column_data: chart_columns.map((col) => {
          const existingCell = existingRow?.column_data?.find(
            (c) => c.column_name === col
          );
          return {
            column_name: col,
            inch: existingCell?.inch || "",
            cm: existingCell?.cm || "",
            code: base.code,
            printable_name: base.printable_name,
            id: existingCell?.id || undefined,
            description: "",
            used: true,
          };
        }),
      };
    });

    setValue("size_chart_data", newData);
  }, [sizes, chart_columns, getValues, setValue]);

  const rows = watch("size_chart_data") || [];

  const handleAutoConvert = (val, fieldName) => {
    if (val === "" || val === null || isNaN(Number(val))) return;
    const num = Number(val);

    const regex = /size_chart_data\.(\d+)\.column_data\.(\d+)\./;
    const match = fieldName.match(regex);
    if (!match) return;

    const rowIndex = Number(match[1]);
    const colIndex = Number(match[2]);
    const columnName = chart_columns[colIndex];

    setValue(
      `size_chart_data.${rowIndex}.column_data.${colIndex}.column_name`,
      columnName
    );

    if (fieldName.endsWith(".inch")) {
      setValue(fieldName, num); // ensure inch is number
      setValue(
        fieldName.replace(".inch", ".cm"),
        Number((num * 2.54).toFixed(2))
      );
    }

    if (fieldName.endsWith(".cm")) {
      setValue(fieldName, num); // ensure cm is number
      setValue(
        fieldName.replace(".cm", ".inch"),
        Number((num / 2.54).toFixed(2))
      );
    }
  };

  // console.log(chart_columns,rows, "chart_columns");
  return (
    <Box sx={{ mt: 2 }}>
      <Typography color="primary.main" fontSize={16} fontWeight={500} mb={1}>
        Size chart (Optional)
      </Typography>

      {/* Column selector */}
      <FieldGrid sx={{ alignItems: "center" }}>
        <Input.Select
          methods={methods}
          label="Column"
          name="chart_columns"
          placeholder="Select Column"
          multiple
          options={columnOptions}
          getKey="printable_name"
          setKey="id"
        />
      </FieldGrid>

      {chart_columns.length === 0 ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <Typography>Choose column first to fill size chart</Typography>
        </Box>
      ) : (
        <TableContainer variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                {chart_columns.map((c, i) => (
                  <TableCell key={i} sx={{ fontWeight: 700 }}>
                    {idToName(c, columnOptions)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell>
                    {idToName(row.size, sizesOptions, "size_label")}
                  </TableCell>

                  {chart_columns.map((col, colIndex) => {
                    let columnDataIndex = row.column_data.findIndex(
                      (x) => String(x.column_name) === String(col)
                    );
                    // if (columnDataIndex === -1) return null;
                    // If not found, create a placeholder
                    if (columnDataIndex === -1) {
                      row.column_data.push({
                        column_name: col,
                        inch: "",
                        cm: "",
                        id: null,
                        code: row.code,
                        printable_name: row.printable_name,
                        used: true,
                      });
                      columnDataIndex = row.column_data.length - 1;
                    }
                    const inchField = `size_chart_data.${rowIndex}.column_data.${columnDataIndex}.inch`;
                    const cmField = `size_chart_data.${rowIndex}.column_data.${columnDataIndex}.cm`;

                    return (
                      <TableCell key={col}>
                        <Box
                          sx={{ display: "flex", gap: 1, overflowY: "auto" }}
                        >
                          <Box sx={{ width: "150px" }}>
                            <Typography variant="caption" fontWeight={600}>
                              Inch
                            </Typography>
                            <Input.Number
                              methods={methods}
                              placeholder="Inch"
                              name={inchField}
                              onValueChange={handleAutoConvert}
                            />
                          </Box>

                          <Box sx={{ width: "150px" }}>
                            <Typography variant="caption" fontWeight={600}>
                              Cm
                            </Typography>
                            <Input.Number
                              methods={methods}
                              placeholder="Cm"
                              name={cmField}
                              onValueChange={handleAutoConvert}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Input.File
        methods={methods}
        name="size_chart"
        label="Size chart photo"
        placeholder="Size chart photo"
        required
      />
    </Box>
  );
};

export default SizeChartTable;
